import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { AuthContext } from "../../../contexts/AuthContext";
import { proceduceService } from "../../../services/api/procedureService";
import CustomTable from "../components/CustomTable";
import Loading from "../components/Loading";
import SearchAndFilterBar from "../components/SearchAndFilterBar";
import ProcedureFormPopup from "./components/ProcedureFormPopup";
import ConfirmDeleteModal from "../components/ConfirmDeleteModal";

const columns = [
  {
    key: "id",
    title: "STT",
    minWidth: "20px",
    align: "text-left",
    render: (value) => <div className="pl-2">{value}</div>,
  },
  { key: "category", title: "Loại dịch vụ", minWidth: "150px" },
  {
    key: "ten",
    title: "Tên thủ tục",
    minWidth: "200px",
    render: (value, item) => (
      <p className={item.isDraft ? "text-red-500" : ""}>{value}</p>
    ),
  },
  { key: "ngayNopHoSo", title: "Thời gian nộp hồ sơ", minWidth: "150px" },
  { key: "ngayThamDinh", title: "Thời gian thẩm định", minWidth: "150px" },
  {
    key: "fileUrl",
    title: "Tài liệu",
    minWidth: "150px",
    render: (value) =>
      value ? (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-500 hover:underline"
        >
          Tài liệu
        </a>
      ) : null,
  },
];

const ProcedureMangement = () => {
  const navigate = useNavigate();
  const { token, user } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();

  const [tableLoading, setTableLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  const [procedures, setProcedures] = useState([]);
  const [deleteId, setDeleteId] = useState(null);

  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMode, setPopupMode] = useState("create");
  const [editingId, setEditingId] = useState(null);

  const currentPage = Number(searchParams.get("page")) || 1;
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const [draftSearch, setDraftSearch] = useState(
    searchParams.get("search") || "",
  );
  const [search, setSearch] = useState(draftSearch);

  /* ================== DATA FORMAT ================== */
  const formatData = useCallback(
    (list) =>
      list.map((item, index) => ({
        id: itemsPerPage * (currentPage - 1) + index + 1,
        category: item.categoryId?.categoryName || "--",
        ten: item.name || "--",
        ngayNopHoSo: item.submissionTime || "--",
        ngayThamDinh: item.reviewTime || "--",
        fileUrl: item.fileUrl || null,
        nav_id: item._id,
        isDraft: item.isDraft,
      })),
    [itemsPerPage, currentPage],
  );

  /* ================== FETCH ================== */
  const fetchData = useCallback(async () => {
    try {
      setTableLoading(true);
      const res = await proceduceService.get(
        search,
        currentPage,
        itemsPerPage,
        token,
      );
      setTotalPages(res.data.totalPages || 1);
      setProcedures(formatData(res.data.procedures || []));
    } catch (e) {
      toast.error("Không thể tải danh sách thủ tục.");
    } finally {
      setTableLoading(false);
    }
  }, [search, currentPage, itemsPerPage, token, formatData]);

  useEffect(() => {
    fetchData();
    setCanDelete(user?.role === "SuperAdmin");
  }, [fetchData, user?.role]);

  /* ================== SEARCH ================== */
  useEffect(() => {
    if (draftSearch === search) return;
    const t = setTimeout(() => {
      setSearch(draftSearch);
      const p = new URLSearchParams(searchParams);
      draftSearch ? p.set("search", draftSearch) : p.delete("search");
      p.set("page", "1");
      setSearchParams(p);
    }, 600);
    return () => clearTimeout(t);
  }, [draftSearch]);

  /* ================== DELETE ================== */
  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      setDeleteLoading(true);
      await proceduceService.delete(deleteId, token);
      toast.success("Xóa thủ tục thành công!");
      fetchData();
    } catch {
      toast.error("Xóa thủ tục thất bại.");
    } finally {
      setDeleteLoading(false);
      setDeleteId(null);
    }
  };

  return (
    <div className="flex flex-col h-full min-h-0 gap-6 relative">
      {/* Header/Search: cố định */}
      <div className="shrink-0">
        <SearchAndFilterBar
          searchPlaceholder="Tìm kiếm thủ tục"
          searchValue={draftSearch}
          onSearchChange={(e) => setDraftSearch(e.target.value)}
          showFilter={false}
          showDatePickers={false}
          onAddClick={() => {
            setPopupMode("create");
            setEditingId(null);
            setPopupOpen(true);
          }}
          addButtonLabel="Thêm mới"
        />
      </div>

      {/* ===== TABLE WRAPPER: outer visible, inner scroll + buffer ===== */}
      <div className="flex-1 min-h-0 overflow-visible">
        <div className="h-full min-h-0 overflow-auto p-[2px]">
          <CustomTable
            data={procedures}
            columns={columns}
            currentPage={currentPage}
            setCurrentPage={(page) => {
              const p = new URLSearchParams(searchParams);
              p.set("page", page.toString());
              setSearchParams(p);
            }}
            totalPages={totalPages}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            loading={tableLoading}
            setView
            setEdit
            setDelete={canDelete}
            handleEdit={(id) => {
              setPopupMode("edit");
              setEditingId(id);
              setPopupOpen(true);
            }}
            handleDelete={(id) => setDeleteId(id)}
            onRowClick={(item) =>
              navigate(`/quan-ly-thu-tuc/thu-tuc/${item.nav_id}`)
            }
          />
        </div>
      </div>

      {/* FORM MODAL */}
      {popupOpen && (
        <ProcedureFormPopup
          mode={popupMode}
          procedureId={editingId}
          closePopup={() => {
            setPopupOpen(false);
            setPopupMode("create");
            setEditingId(null);
          }}
          onSuccess={fetchData}
        />
      )}

      {/* CONFIRM DELETE MODAL */}
      <ConfirmDeleteModal
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={confirmDelete}
        loading={deleteLoading}
      />
    </div>
  );
};

export default ProcedureMangement;
